import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { apiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Key,
  ShieldAlert,
  UserCheck,
  UserX,
} from 'lucide-react';

const ROLE_META = {
  admin: {
    label: 'Admin',
    className: 'bg-black text-white',
    desc: 'Everything, including Users, Workforce and deleting records',
  },
  accountant: {
    label: 'Accountant',
    className: 'bg-gray-200 text-gray-800',
    desc: 'Create and edit everything (clients, invoices, payments, expenses, settings). Cannot delete, cannot see Workforce or Users',
  },
  viewer: {
    label: 'Viewer',
    className: 'bg-gray-100 text-gray-700',
    desc: 'Read-only. Cannot see Workforce or Users',
  },
};

// Role picker + a line saying what the picked role can do.
function RoleField({ id, value, onChange }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Role</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(ROLE_META).map(([key, meta]) => (
            <SelectItem key={key} value={key}>{meta.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-gray-500">{ROLE_META[value]?.desc}</p>
    </div>
  );
}

const EMPTY_NEW = () => ({ email: '', name: '', role: 'accountant', password: '' });

export default function Users() {
  const { user: me, can, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_NEW());
  const [savingAdd, setSavingAdd] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'accountant', is_active: true });
  const [savingEdit, setSavingEdit] = useState(false);

  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  // If a non-admin lands here, show a clean denial instead of API errors.
  const amAdmin = can('users');

  const fetchData = useCallback(async () => {
    if (!amAdmin) {
      setLoading(false);
      return;
    }
    try {
      const res = await usersAPI.list();
      setUsers(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, [amAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!amAdmin) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center">
        <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
        <h1 className="text-xl font-semibold mb-2">Admins only</h1>
        <p className="text-sm text-gray-500">You don't have permission to manage users.</p>
      </div>
    );
  }

  const openNew = () => {
    setAddForm(EMPTY_NEW());
    setAddOpen(true);
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.email.trim() || !addForm.name.trim() || !addForm.password) {
      return toast.error('Email, name and password are required');
    }
    if (addForm.password.length < 6) {
      return toast.error('Password must be at least 6 characters');
    }
    setSavingAdd(true);
    try {
      const name = addForm.name.trim();
      await usersAPI.create({ ...addForm, name, email: addForm.email.trim().toLowerCase() });
      toast.success(`User ${name} created`);
      setAddOpen(false);
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Failed to create user'));
    } finally {
      setSavingAdd(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      name: u.name || '',
      role: u.role || 'viewer',
      is_active: u.is_active !== false,
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      await usersAPI.update(editTarget.id, { ...editForm, name: editForm.name.trim() });
      toast.success('User updated');
      setEditTarget(null);
      if (editTarget.id === me?.id) {
        // Edited myself: reload permissions so the UI matches the new role.
        await checkAuth();
        if (editForm.role !== 'admin') return navigate('/dashboard');
      }
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Failed to update'));
    } finally {
      setSavingEdit(false);
    }
  };

  const openPw = (u) => {
    setPwTarget(u);
    setPwValue('');
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (!pwTarget) return;
    if (pwValue.length < 6) {
      return toast.error('Password must be at least 6 characters');
    }
    setSavingPw(true);
    try {
      await usersAPI.resetPassword(pwTarget.id, pwValue);
      toast.success(`Password reset for ${pwTarget.name}`);
      setPwTarget(null);
    } catch (err) {
      toast.error(apiError(err, 'Failed to reset password'));
    } finally {
      setSavingPw(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await usersAPI.delete(u.id);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Failed to delete'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Users</h1>
          <p className="text-sm text-gray-500 mt-1">Manage who can log in to PriceIntel and what they can do.</p>
        </div>
        <Button onClick={openNew} className="bg-black hover:bg-black/90 text-white">
          <Plus size={16} className="mr-2" />
          Add user
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All users</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading…
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No users yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                  <tr>
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Email</th>
                    <th className="py-3 pr-4">Role</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isMe = u.id === me?.id;
                    const roleMeta = ROLE_META[u.role] || ROLE_META.viewer;
                    return (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3 pr-4 font-medium truncate max-w-[220px]">
                          {u.name}
                          {isMe && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                        </td>
                        <td className="py-3 pr-4 text-gray-600 truncate max-w-[220px]" title={u.email}>{u.email}</td>
                        <td className="py-3 pr-4">
                          <Badge className={`${roleMeta.className} hover:${roleMeta.className}`} title={roleMeta.desc}>
                            {roleMeta.label}
                          </Badge>
                        </td>
                        <td className="py-3 pr-4">
                          {u.is_active === false ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600">
                              <UserX size={14} /> Disabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700">
                              <UserCheck size={14} /> Active
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(u)}
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPw(u)}
                              title="Reset password"
                            >
                              <Key size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(u)}
                              disabled={isMe}
                              title={isMe ? "You can't delete yourself" : 'Delete'}
                              className="text-red-700 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              Share the password with the new user over WhatsApp or email. They can change it later from their account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="add-name">Full name</Label>
              <Input
                id="add-name"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. Anita Sharma"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-email">Email</Label>
              <Input
                id="add-email"
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                placeholder="accountant@deseno.co.in"
                required
              />
            </div>
            <RoleField id="add-role" value={addForm.role} onChange={(v) => setAddForm({ ...addForm, role: v })} />
            <div className="space-y-2">
              <Label htmlFor="add-pw">Temporary password</Label>
              <Input
                id="add-pw"
                type="text"
                value={addForm.password}
                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                placeholder="At least 6 characters"
                required
              />
              <p className="text-xs text-gray-500">
                Shown in plain text so you can copy and share it. The user can change it after logging in.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={savingAdd}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-black/90 text-white" disabled={savingAdd}>
                {savingAdd && <Loader2 size={14} className="animate-spin mr-2" />}
                Create user
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>{editTarget?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <RoleField id="edit-role" value={editForm.role} onChange={(v) => setEditForm({ ...editForm, role: v })} />
            <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-gray-500">Disabled users cannot log in.</p>
              </div>
              <Switch
                checked={editForm.is_active}
                onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })}
                disabled={editTarget?.id === me?.id}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)} disabled={savingEdit}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-black/90 text-white" disabled={savingEdit}>
                {savingEdit && <Loader2 size={14} className="animate-spin mr-2" />}
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-medium">{pwTarget?.name}</span>. Share it with them; they can change it from their account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPw} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pw-new">New password</Label>
              <Input
                id="pw-new"
                type="text"
                value={pwValue}
                onChange={(e) => setPwValue(e.target.value)}
                placeholder="At least 6 characters"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPwTarget(null)} disabled={savingPw}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-black/90 text-white" disabled={savingPw}>
                {savingPw && <Loader2 size={14} className="animate-spin mr-2" />}
                Reset password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
